import { useRef, useState, type FC, type FormEvent } from "react"
import type { Integration, ExtraCard } from "../models/dashboard"

type CardData = Integration | ExtraCard

interface IntegrationCardProps {
  integration: CardData
}

const COLORS: Record<string, string> = {
  "10": "#f59e0b",
  "14": "#10b981",
  "2": "#14b8a6",
  "3": "#eab308",
  "4": "#f59e0b",
  "5": "#0ea5e9",
  "6": "#8b5cf6",
  "9": "#8b5cf6",
  "11": "#8b5cf6",
  "12": "#10b981",
  "13": "#0ea5e9",
  "1": "#f97316",
  "7": "#06b6d4",
  offers: "#8b5cf6",
  intranetNewtour: "#3b82f6",
}

const IntegrationCard: FC<IntegrationCardProps> = ({ integration }) => {
  const formRef = useRef<HTMLFormElement>(null)
  const [showModal, setShowModal] = useState(false)
  const typeKey = "type" in integration ? String(integration.type) : integration.id
  const color = COLORS[typeKey] ?? "#6b7280"
  const icon = integration.icon
  const name = integration.type_label
  const description = integration.description
  
  const isIntegration = "auth_type" in integration && "type" in integration
  const auth_type = isIntegration ? (integration as Integration).auth_type : "direct"
  const url = integration.url
  const user = isIntegration ? (integration as Integration).user : null
  const pass = isIntegration ? (integration as Integration).pass : null
  const controllerAction = isIntegration ? (integration as Integration).controller_action : null
  const extraFields = isIntegration ? (integration as Integration).extra_fields : null

  const renderAction = () => {
    switch (auth_type) {
      case "direct":
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
            style={{ color }}
          >
            Ver + <i className="fa fa-external-link" />
          </a>
        )

      case "get_params": {
        const params = new URLSearchParams()
        if (extraFields?.accion) params.append("accion", String(extraFields.accion))
        const usernameField = (extraFields?.username_field as string) ?? "username"
        const passwordField = (extraFields?.password_field as string) ?? "password"
        if (user) params.append(usernameField, user)
        if (pass) params.append(passwordField, pass)
        const href = `${url}?${params.toString()}`
        return (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
            style={{ color }}
          >
            Ver + <i className="fa fa-external-link" />
          </a>
        )
      }

      case "form_post": {
        const usernameField = (extraFields?.username_field as string) ?? "username"
        const passwordField = (extraFields?.password_field as string) ?? "password"
        return (
          <>
            <form ref={formRef} action={url} method="post" target="_blank" className="hidden">
              {extraFields?.accion != null && (
                <input type="hidden" name="accion" value={String(extraFields.accion)} />
              )}
              <input type="hidden" name={usernameField} value={user ?? ""} />
              <input type="hidden" name={passwordField} value={pass ?? ""} />
            </form>
            <button
              type="button"
              onClick={() => formRef.current?.submit()}
              className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
              style={{ color }}
            >
              Ver + <i className="fa fa-external-link" />
            </button>
          </>
        )
      }

      case "controller":
        return controllerAction ? (
          <a
            href={controllerAction}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
            style={{ color }}
          >
            Ver + <i className="fa fa-external-link" />
          </a>
        ) : null

      case "ajax": {
        const usernameField = (extraFields?.username_field as string) ?? "username"
        const passwordField = (extraFields?.password_field as string) ?? "password"
        const handleClick = async () => {
          const formData = new FormData()
          formData.append(usernameField, user ?? "")
          formData.append(passwordField, pass ?? "")
          try {
            await fetch(`${url}/automatic-start/`, {
              method: "POST",
              body: formData,
              mode: "no-cors",
            })
          } catch {
            // silently fail
          }
          window.open(url, "_blank")
        }
        return (
          <button
            type="button"
            onClick={handleClick}
            className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
            style={{ color }}
          >
            Ver + <i className="fa fa-external-link" />
          </button>
        )
      }

      case "modal":
        return (
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
              style={{ color }}
            >
              Acessos <i className="fa fa-eye" />
            </button>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium hover:opacity-80"
              style={{ color }}
            >
              Ver + <i className="fa fa-external-link" />
            </a>
          </div>
        )

      default:
        return null
    }
  }

  const handleModalClose = (e: FormEvent) => {
    e.preventDefault()
    setShowModal(false)
  }

  return (
    <>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="p-3" style={{ borderLeft: `3px solid ${color}` }}>
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: color }}
            >
              <i className={`fa ${icon} text-white text-sm`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
              {description && (
                <p className="text-xs text-gray-500 truncate">{description}</p>
              )}
            </div>
          </div>
          <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: "60%", backgroundColor: color }}
            />
          </div>
          <div className="mt-1.5 flex justify-end">{renderAction()}</div>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-medium text-sm text-gray-900">Acessos {name}</h3>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fa fa-times" />
              </button>
            </div>
            <div className="px-4 py-4 space-y-3">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Utilizador</p>
                <p className="text-sm font-medium text-gray-900">{user ?? "\u00A0"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Password</p>
                <p className="text-sm font-medium text-gray-900">{pass ?? "\u00A0"}</p>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default IntegrationCard
